import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { z } from "zod";
import { ArrowLeft, CheckCircle2, Loader2, Mail, Radar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { BUSINESS } from "@/lib/business";

const EMAIL_REGEX = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
const schema = z.object({
  email: z
    .string()
    .trim()
    .min(3, "Add your email so we can reply.")
    .max(254, "Email is too long.")
    .regex(EMAIL_REGEX, "That doesn't look like a valid email."),
  note: z.string().trim().max(500, "Please keep your message under 500 characters.").optional(),
});

const LaunchList = () => {
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success">("idle");
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const parsed = schema.safeParse({ email, note });
    if (!parsed.success) {
      setError(parsed.error.issues[0].message);
      return;
    }
    setStatus("loading");
    const { data, error: fnError } = await supabase.functions.invoke("early-bird-signup", {
      body: {
        email: parsed.data.email.toLowerCase(),
        note: parsed.data.note || "",
        source: "launch_list",
      },
    });
    const code = (data as { error?: string } | null)?.error;
    if (fnError || code) {
      setStatus("idle");
      setError(
        code === "invalid_email"
          ? "That doesn't look like a valid email."
          : "Something glitched on our end. Try again in a moment.",
      );
      return;
    }
    setStatus("success");
  };

  return (
    <>
      <Helmet>
        <title>Early-Bird Launch List | Trends Discovery</title>
        <meta
          name="description"
          content="Join the Trends Discovery early-bird launch list: first access to new milestones, plus a direct line for questions about the $39 lifetime licence."
        />
        <link rel="canonical" href="https://trendsdiscovery.com/launch-list" />
        <meta property="og:title" content="Early-bird launch list — Trends Discovery" />
        <meta
          property="og:description"
          content="Sign up for first access to the next Trends Discovery milestone and send your questions straight to the team."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://trendsdiscovery.com/launch-list" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <main className="min-h-screen bg-background text-foreground">
        <div className="container max-w-2xl py-16">
          <Link
            to="/launch"
            className="inline-flex items-center gap-2 font-mono text-xs text-primary hover:underline"
          >
            <ArrowLeft className="w-3 h-3" /> BACK TO THE COUNTDOWN
          </Link>

          <div className="mt-8 flex items-center gap-2">
            <span className="grid place-items-center w-7 h-7 rounded bg-primary text-primary-foreground">
              <Radar className="w-4 h-4" />
            </span>
            <span className="font-display font-semibold">Trends.Discovery</span>
          </div>

          <p className="mt-8 font-mono text-xs tracking-[0.2em] text-primary">EARLY-BIRD LAUNCH LIST</p>
          <h1 className="mt-3 font-display text-4xl md:text-5xl font-bold">
            One list. Every launch update.
          </h1>
          <p className="mt-4 text-muted-foreground leading-relaxed">
            Sign up once and we'll email you the moment each roadmap milestone goes live — plus first
            access while it is still in testing. Add a question and it reaches us directly.
          </p>

          <section className="mt-10 rounded-2xl border border-primary/30 bg-primary/5 p-6">
            {status === "success" ? (
              <div>
                <p className="inline-flex items-center gap-2 text-sm text-primary">
                  <CheckCircle2 className="w-4 h-4" aria-hidden="true" /> You're on the list — check
                  your inbox for the confirmation.
                </p>
                <p className="mt-3 text-sm text-muted-foreground">
                  If you asked a question, we'll reply from {BUSINESS.email}.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label htmlFor="launch-list-email" className="text-sm font-medium">
                    Email address
                  </label>
                  <Input
                    id="launch-list-email"
                    type="email"
                    autoComplete="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    aria-invalid={!!error}
                    aria-describedby={error ? "launch-list-error" : undefined}
                    className="mt-2"
                  />
                </div>
                <div>
                  <label htmlFor="launch-list-note" className="text-sm font-medium">
                    Your question <span className="text-muted-foreground">(optional)</span>
                  </label>
                  <Textarea
                    id="launch-list-note"
                    placeholder="Anything you want to know before launch?"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    maxLength={500}
                    rows={4}
                    className="mt-2"
                  />
                </div>
                <Button type="submit" disabled={status === "loading"} className="w-full sm:w-auto">
                  {status === "loading" ? (
                    <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                  ) : (
                    <>
                      <Mail className="w-4 h-4" aria-hidden="true" /> Join the launch list
                    </>
                  )}
                </Button>
              </form>
            )}
            {error && (
              <p id="launch-list-error" className="mt-3 text-sm text-destructive">
                {error}
              </p>
            )}
          </section>

          <div className="mt-10 flex flex-wrap gap-4 text-sm">
            <Link to="/launch" className="text-primary hover:underline">
              Launch countdown
            </Link>
            <Link to="/roadmap" className="text-primary hover:underline">
              Full roadmap
            </Link>
            <Link to="/#pricing" className="text-primary hover:underline">
              Get the $39 lifetime licence
            </Link>
          </div>
        </div>
      </main>
    </>
  );
};

export default LaunchList;
